import { useCallback, useState } from 'react';
import { Button, SearchAutocomplete, TextArea, useToast } from '@aimsos/ui';
import { useTranslation } from '@aimsos/i18n';
import { useWorkspaceUsers } from '../../hooks/useWorkspaceUsers.js';

/**
 * The composer sits flush on the panel surface, so the people field has to drop
 * its default `bg-surface` — that's the panel's own colour and the field would
 * disappear into it. `TextArea` already ships `bg-surface-muted`; this aligns
 * the pair. The child selector is required because `SearchAutocomplete` styles
 * its own inner container, not the root element that takes `className`.
 */
const PEOPLE_FIELD_CLASS = '[&>div]:!border-edge [&>div]:!bg-surface-muted';

function ThreadComposer({ onPost, placeholder, silentPost = false }) {
  const { t } = useTranslation('work-queues-app');
  const toast = useToast();
  const [peopleSearch, setPeopleSearch] = useState('');
  const [draft, setDraft] = useState('');
  const [peopleIds, setPeopleIds] = useState([]);
  // Real workspace directory (CT-5094) for @mentions — not mock people. The typed
  // query goes to the server (search.keyword) instead of only filtering the
  // cached page client-side, which never scaled past its 100-row cap (CT-5176).
  // `peopleIds` (selectedIds) keeps an already-@mentioned person from vanishing
  // from `options` when the search that surfaced them clears and refetches.
  const { users } = useWorkspaceUsers(true, peopleSearch, peopleIds);

  const handleDraftChange = useCallback((event) => {
    setDraft(event.target.value);
  }, []);

  const handlePost = useCallback(() => {
    if (!draft.trim()) return;
    const mentions = users.filter((person) => peopleIds.includes(person.id));
    const invited = mentions.map((person) => person.name).join(', ');
    // TODO(CT-4867 / backend): posting a comment must persist to the audit-thread
    // endpoint, and any @mentions in `draft` must trigger an email notification to
    // the mentioned participants (backend + email template — meeting-agreed delta,
    // pending coordination with backend). Local append only for V1.
    onPost?.({ body: draft.trim(), mentions });
    if (!silentPost) {
      toast.success({
        title: t('thread.postedTitle'),
        message: invited
          ? t('thread.postedWithPeopleMessage', { people: invited })
          : t('thread.postedMessage'),
      });
    }
    setDraft('');
    setPeopleIds([]);
    setPeopleSearch('');
  }, [draft, onPost, peopleIds, silentPost, users, t, toast]);

  return (
    <div className="flex flex-col gap-2">
      <SearchAutocomplete
        isMulti
        options={users}
        selectedValues={peopleIds}
        onSelectedValuesChange={setPeopleIds}
        onInputValueChange={setPeopleSearch}
        placeholder={t('thread.addPeoplePlaceholder')}
        emptyMessage={t('thread.noPeopleFound')}
        dropdownMaxHeight="12rem"
        className={PEOPLE_FIELD_CLASS}
        // The composer sits at the bottom of the panel, so the options must open
        // UPWARD (bottom-anchored) or they'd be clipped below the viewport.
        dropdownClassName="bottom-full top-auto mb-1 !mt-0"
      />
      <TextArea
        rows={3}
        value={draft}
        onChange={handleDraftChange}
        placeholder={placeholder ?? t('thread.placeholder')}
      />
      <div className="flex items-end justify-between gap-3">
        <p className="min-w-0 text-xs text-content-faint">{t('thread.notice')}</p>
        <Button
          variant="primary"
          size="sm"
          className="shrink-0"
          disabled={!draft.trim()}
          onClick={handlePost}
        >
          {t('thread.post')}
        </Button>
      </div>
    </div>
  );
}

export default ThreadComposer;
